import { Button, Card, Col, Container, Row } from "reactstrap";
// import CreateFormModal from "../Services/components/createFormModal";
// import CreateSubServiceModal from "../Services/components/createSubServicesModal";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  deleteFormDataThunk,
  getFormData,
} from "../../../store/slices/formBuilder";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useDebounce } from "../../../Hooks/useDebounce";
import {
  Box,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FaWpforms } from "react-icons/fa";
import { faEye, faPencil, faSearch, faTrash } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import { Breadcrumbs } from "../../../AbstractElements";

export default function FormManagmentPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);

  /****handle open model */
  const handleOpenModel = () => {
    navigate(`${process.env.PUBLIC_URL}/pages/form-management/create`);
  };

  /***Row Action */
  const rowAction = (type, data) => {
    if (type === "DELETE") {
      Swal.fire({
        title: "Are you sure?",
        text: "This action will deactivate the form.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "Cancel",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const response = await dispatch(deleteFormDataThunk(data?.ID));
            if (response.success || !response.error) {
              Swal.fire("Deleted!", "The form has been removed.", "success");
              fetchData(debouncedSearch);
            }
          } catch (error) {
            toast.error("Failed to delete the form.");
          }
        }
      });
    } else if (type === "EDIT") {
      navigate(
        `${process.env.PUBLIC_URL}/pages/form-management/edit/${data.ID}`,
        { state: { data } },
      );
    } else if (type === "VIEW") {
      navigate(
        `${process.env.PUBLIC_URL}/pages/form-management/view/${data.ID}`,
        { state: { data } },
      );
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "Sr", // Virtual column
        header: "S.No",
        size: 80,
        enableSorting: false,
        Cell: ({ row }) => {
          return (page - 1) * pageSize + row.index + 1;
        },
      },
      {
        accessorKey: "name",
        header: "Name",
        size: 250,
        enableSorting: false,
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue()}>
            <span
              style={{
                display: "block",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {cell.getValue()}
            </span>
          </Tooltip>
        ),
      },
      {
        accessorKey: "action",
        header: "Actions",
        size: 150,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnActions: false,
        Cell: ({ row }) => (
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Button
              outline
              color="info"
              size="sm"
              onClick={() => {
                rowAction("VIEW", row?.original);
              }}
              title="View Form"
            >
              <FontAwesomeIcon data-action-type="edit" icon={faEye} />
            </Button>
            <Button
              outline
              color="primary"
              size="sm"
              onClick={() => {
                rowAction("EDIT", row?.original);
              }}
              title="Edit Form"
            >
              <FontAwesomeIcon data-action-type="edit" icon={faPencil} />
            </Button>
            <Button
              outline
              color="danger"
              size="sm"
              onClick={() => {
                rowAction("DELETE", row?.original);
              }}
              title="Delete Form"
            >
              <FontAwesomeIcon data-action-type="delete" icon={faTrash} />
            </Button>
            <Button
              outline
              color="warning"
              size="sm"
              onClick={() => {
                handleServiceMappingOpen(row?.original);
              }}
              title="View Service Mapping"
            >
              <FaWpforms />
              {/* <FontAwesomeIcon data-action-type="FaWpforms" icon={FaWpforms} /> */}
            </Button>
          </div>
        ),
      },
    ],
    [data],
  );

  const fetchData = async (searchQuery) => {
    setIsLoading(true);
    try {
      const response = await dispatch(
        getFormData({
          limit: pageSize,
          page: page,
          searchQuery: searchQuery,
        }),
      );
      console.log("response.payload.", response.payload);
      if (response?.payload?.data) {
        setData(response?.payload?.data || []);
        setTotalDataCount(response?.payload?.total);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      toast.error("Failed to fetch users.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (searchQuery) {
      fetchData(debouncedSearch);
    } else {
      fetchData();
    }
  }, [page, pageSize, searchQuery, debouncedSearch]);

  const handlePageChange = (updater) => {
    // updater can be a function or an object from MRT
    const nextState =
      typeof updater === "function"
        ? updater({ pageIndex: page - 1, pageSize: pageSize })
        : updater;

    // Check if pageSize actually changed
    if (nextState.pageSize !== pageSize) {
      setPageSize(nextState.pageSize);
      setPage(1); // Reset to first page if limit changes
    } else {
      setPage(nextState.pageIndex + 1); // Convert 0-based back to 1-based
    }
  };

  const handleOnChangeSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };

  const table = useMaterialReactTable({
    columns,
    data,
    initialState: { density: "compact" },
    enableStickyHeader: true,
    manualPagination: true,
    rowCount: totalDataCount,
    onPaginationChange: handlePageChange,
    state: {
      pagination: { pageIndex: page, pageSize: pageSize },
      isLoading: isLoading,
    },
    muiSkeletonProps: {
      animation: "wave",
      height: 28, // Set your desired height in pixels
      sx: {
        borderRadius: "4px", // Optional: match your table's design
      },
    },
    enableColumnFilters: false,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableColumnActions: false,
    enableGlobalFilter: false,
    // 🗂 allow column visibility toggle menu
    enableColumnVisibility: false,
    enableColumnVisibilityButton: false,
    enableHiding: false,

    renderTopToolbarCustomActions: () => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between", // 👈 left vs right
          alignItems: "center",
          width: "100%",
          p: "0.5rem",
        }}
      >
        {/* 🔎 Left side: custom search */}
        <Box>
          <TextField
            variant="outlined"
            placeholder="Search by name"
            size="small"
            sx={{
              minWidth: "280px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                "& fieldset": {
                  borderColor: searchQuery ? "primary.main" : "grey.400",
                  // ✅ primary if data, muted if empty
                },
                "&:hover fieldset": {
                  borderColor: "primary.main", // ✅ primary on hover
                },
                "&.Mui-focused fieldset": {
                  borderColor: "primary.main", // ✅ primary when focused
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FontAwesomeIcon
                    icon={faSearch}
                    style={{
                      color: searchQuery ? "#1976d2" : "#9e9e9e",
                      // ✅ primary if data, muted if empty
                    }}
                  />
                  {/* ✅ primary blue icon */}
                </InputAdornment>
              ),
            }}
            onChange={handleOnChangeSearch}
          />
        </Box>

        {/* 👉 Right side: buttons */}
        <Box sx={{ display: "flex", gap: "0.5rem" }}>
          <div>
            <Button onClick={handleOpenModel} color="primary">
              Add Form
            </Button>
          </div>
        </Box>
      </Box>
    ),
  });

  const handleServiceMappingOpen = (data) => {
    navigate(
      `${process.env.PUBLIC_URL}/pages/form-management/mapping/${data.ID}`,
      { state: { data } },
    );
  };

  console.log("data", data);
  return (
    <div>
      {/**Header Form Management */}
      <Breadcrumbs
        mainTitle="Form Management"
        parent="Form Management"
        parentPath="form-management"
        title="Form Management List"
      />

      {/*****Model */}
      {/* <FormCreateEdit
                isOpen={openIsModel}
                toggle={() =>
                    setIsModel(preve => !preve)
                }
            /> */}

      {/***Table Data */}
      <div>
        <Container>
          <Card>
            <MaterialReactTable table={table} />
          </Card>
        </Container>
      </div>

      {/* <CreateFormModal
                // isOpen={true}
                toggle={()=>{}}
                // editForm={
                // selectedForm?.formFields ? JSON.parse(selectedForm?.formFields) : []
                // }
                // formData={selectedForm}
                // selectedSubService={selectedSubService}
            /> */}
    </div>
  );
}
